/**
 * lib/stationing.js — Iter 50 client helpers for Alignment + Stationing.
 *
 * Thin wrappers over the `/api/sheets/{id}/stations/*` endpoints. Single
 * source of truth for every surface that needs to render or jump to a
 * STA value (GPS overlay, structure inspector, measurement chips, search
 * palette, revision impact banner).
 *
 * Resolutions are short-lived; we cache them in-memory per (sheet, x, y)
 * for ~30s so a fast scrub doesn't pummel the backend.
 */
import { api } from "@/lib/api";

const _RESOLVE_CACHE = new Map();   // key -> { exp, value }
const _TTL_MS = 30_000;

const _key = (sheetId, nx, ny, alignmentId) =>
  `${sheetId}:${nx.toFixed(4)}:${ny.toFixed(4)}:${alignmentId || ""}`;

/** Resolve a sheet-relative (nx, ny) → { resolved, station_label, offset_label, … } */
export async function resolveStationOnSheet(sheetId, nx, ny, { alignmentId, signal } = {}) {
  if (!sheetId || nx == null || ny == null) return { resolved: false };
  const k = _key(sheetId, nx, ny, alignmentId);
  const cached = _RESOLVE_CACHE.get(k);
  if (cached && cached.exp > Date.now()) return cached.value;
  try {
    const { data } = await api.post(
      `/sheets/${sheetId}/stations/resolve`,
      { sheet_id: sheetId, normalized_x: nx, normalized_y: ny,
        alignment_id: alignmentId || null },
      { signal },
    );
    _RESOLVE_CACHE.set(k, { exp: Date.now() + _TTL_MS, value: data });
    return data;
  } catch (e) {
    if (e?.name === "CanceledError") return { resolved: false };
    return { resolved: false };
  }
}

/** Inverse: STA value or "STA 123+50" → { sheet_id, normalized_x, normalized_y, … } */
export async function pointFromStation(sheetId, { station, station_text, alignmentId }) {
  try {
    const { data } = await api.post(
      `/sheets/${sheetId}/stations/from_station`,
      { station, station_text, alignment_id: alignmentId || null },
    );
    return data;
  } catch {
    return { resolved: false };
  }
}

/** Returns { resolved, begin_label, end_label, alignment_name, units } | { resolved:false } */
export async function fetchRevisionImpactStations(sheetId) {
  try {
    const { data } = await api.get(`/sheets/${sheetId}/revision_impact_stations`);
    return data;
  } catch {
    return { resolved: false };
  }
}

/** Returns list of alignments anchored to a sheet (used by the AlignmentInspector + impact banner). */
export async function listSheetAlignments(sheetId) {
  try {
    const { data } = await api.get(`/sheets/${sheetId}/alignments`);
    return data?.alignments || [];
  } catch {
    return [];
  }
}

/** Display helper — joins station + offset into one calm string, or "—". */
export function formatStationAndOffset(resolved) {
  if (!resolved || resolved.resolved === false) return null;
  const s = resolved.station_label;
  const o = resolved.offset_label;
  if (s && o) return `${s} · ${o}`;
  return s || o || null;
}
